import { ANALYTICS_APPS, type AnalyticsEventName, type PropertiesFor } from '@syncanix/shared';

import type posthogType from 'posthog-js';

/**
 * The marketing site's thin, typed wrapper over `posthog-js`. Unlike the
 * dashboard's, this one is CONSENT-GATED: `initAnalytics` is only ever called
 * after the visitor grants consent (see MarketingAnalyticsProvider), so no
 * analytics cookie is set on a first visit. Every method no-ops until then.
 *
 * `posthog-js` is loaded via dynamic import ONLY after consent (iter 25):
 * eagerly bundling it put ~60 KB gz of analytics code on every visitor's
 * critical path — including the majority who never grant consent. Calls made
 * while the chunk is in flight (e.g. the first pageview, captured right after
 * the grant) are buffered and flushed on load; calls before consent are
 * dropped exactly as before.
 *
 * The marketing surface is anonymous — there is no `identify` here. Events are
 * attributed to a cookie distinct-id, powering the acquisition funnel
 * (pageviews → signup-CTA clicks) without any cross-site or PII tracking.
 */

const DEFAULT_API_HOST = 'https://eu.i.posthog.com';

type Posthog = typeof posthogType;

let ph: Posthog | null = null;
let loading = false;
let optedOut = false;

/** Calls buffered between consent-grant and chunk-load (bounded). */
const PENDING_LIMIT = 32;
const pending: ((posthog: Posthog) => void)[] = [];

function run(fn: (posthog: Posthog) => void): void {
  if (ph !== null) {
    fn(ph);
    return;
  }
  if (loading && pending.length < PENDING_LIMIT) pending.push(fn);
  // Not loading ⇒ pre-consent ⇒ drop (the consent gate).
}

export interface InitMarketingAnalyticsConfig {
  readonly apiKey?: string | undefined;
  readonly apiHost?: string | undefined;
  readonly environment: string;
  readonly locale: string;
}

/**
 * Start PostHog after consent. Synchronous contract preserved: returns whether
 * analytics WILL run (key present); the chunk loads in the background and
 * buffered calls flush when it arrives.
 */
export function initAnalytics(config: InitMarketingAnalyticsConfig): boolean {
  if (ph !== null || loading) return true;
  if (config.apiKey === undefined || config.apiKey === '') return false;
  const apiKey = config.apiKey;
  loading = true;
  optedOut = false;
  void import('posthog-js').then(({ default: posthog }) => {
    posthog.init(apiKey, {
      api_host: config.apiHost ?? DEFAULT_API_HOST,
      person_profiles: 'identified_only',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: true,
      disable_session_recording: true,
    });
    posthog.register({
      app: ANALYTICS_APPS.MARKETING,
      environment: config.environment,
      locale: config.locale,
    });
    ph = posthog;
    if (optedOut) {
      posthog.opt_out_capturing();
      posthog.reset();
      pending.length = 0;
      return;
    }
    const queued = pending.splice(0, pending.length);
    queued.forEach((fn) => {
      fn(posthog);
    });
  });
  return true;
}

function cleanUrl(pathname: string): string {
  if (typeof window === 'undefined') return pathname;
  return window.location.origin + pathname;
}

export const analytics = {
  isEnabled(): boolean {
    return ph !== null || loading;
  },

  capture<N extends AnalyticsEventName>(name: N, properties?: PropertiesFor<N>): void {
    run((posthog) => {
      posthog.capture(name, properties);
    });
  },

  capturePageview(pathname: string): void {
    const url = cleanUrl(pathname);
    run((posthog) => {
      posthog.capture('$pageview', { $current_url: url, $pathname: pathname });
    });
  },

  setLocale(locale: string): void {
    run((posthog) => {
      posthog.register({ locale });
    });
  },

  /**
   * Stop tracking and forget the visitor when consent is withdrawn (or denied
   * after a prior grant). Safe to call when never initialised; if the chunk is
   * still in flight, the buffer is dropped and the opt-out applies on arrival.
   */
  optOut(): void {
    pending.length = 0;
    optedOut = true;
    if (ph !== null) {
      ph.opt_out_capturing();
      ph.reset();
    }
  },
};

export type MarketingAnalytics = typeof analytics;
